import React from 'react';
import PropTypes from 'prop-types';
import './search-bar.scss';

import Image, { ImageSizes } from '../Image/Image';
import BetaIcon, { IconColors, IconSizes } from '../../component/Icons/Icon';

export default function SearchBar({
  className,
  variant,
  width,
  isAutoFocused,
  isElevated,
  isAutoCompleted,
  placeholder,
  value,
  handleClick,
  handleSearch,
  handleOnChange,
  keyboardHandler,
  asButton,
  isWhiteBackground,
  isNavSearch,
}) {
  const getClassNames = () => {
    const prefix = 'search-bar';
    let classNames = prefix;
    if (className) classNames += ' ' + className;
    if (variant) classNames += ' ' + prefix + variant;
    if (isElevated) classNames += ' ' + prefix + '--elevated';
    if (isAutoFocused) classNames += ' ' + prefix + '--focused';
    return classNames;
  };

  const searchIconSize = isNavSearch ? 19 : 24;

  // eslint-disable-next-line no-undef
  const defaultPlaceholder = app_main_wpml.search_an_activity_or_place;

  // TODO: This is an icon therefore should use the Icon component rather than the Image component (Icon component not avialable yet in UI Library)
  const searchIcon = (
    <BetaIcon name={'search'} size={IconSizes[searchIconSize]} />
  );

  const clearIcon = (
    <div onClick={() => handleClear()} className="search-bar__clear-icon">
      <BetaIcon name="close" size={IconSizes[14]} />
    </div>
  );

  const fakeSearchInput = (
    <>
      {value?.length <= 0 || !value ? (
        <div
          role="button"
          className="text-left pr-base pb-base pt-base search-bar__search-input--placeholder"
        >
          {placeholder ? placeholder : defaultPlaceholder}
        </div>
      ) : (
        <div
          role="button"
          className="text-left p-base search-bar__search-input"
        >
          {value}
        </div>
      )}
    </>
  );

  // This was done to prevent the DOM throwing an error about buttons, a button can't be parent of another button even if condionally rendered.
  const fakeSearchButton = (
    <div className="search-bar__search-button">{searchIcon}</div>
  );

  // TODO: To swap with the new Button component and add new style
  const searchButton = (
    <button
      className="search-bar__search-button"
      onClick={() => handleSearch()}
    >
      {searchIcon}
    </button>
  );

  const handleOnFocus = () => {
    const searchBarElements = document.getElementsByClassName('search-bar');
    const lastItem = searchBarElements.length - 1;
    searchBarElements[lastItem].classList?.add('search-bar--focused');
    searchBarElements[lastItem].classList?.remove('search-bar--elevated');
  };

  const handleOnBlur = () => {
    const searchBarElements = document.getElementsByClassName('search-bar');
    const lastItem = searchBarElements.length - 1;
    searchBarElements[lastItem].classList?.remove('search-bar--focused');
    searchBarElements[lastItem].classList?.add('search-bar--elevated');
  };

  const handleClear = () => {
    handleOnChange({ target: { value: '' } });
  };

  const searchInput = (
    <div className="search-bar__search-wrapper">
      <input
        name="search"
        type="search"
        className="search-bar__search-input"
        value={value}
        placeholder={placeholder ? placeholder : defaultPlaceholder}
        onKeyDown={keyboardHandler}
        onChange={(e) => handleOnChange(e)}
        onFocus={
          variant === SearchBarVariants.primary ? () => handleOnFocus() : null
        }
        onBlur={
          variant === SearchBarVariants.primary ? () => handleOnBlur() : null
        }
        autoFocus={isAutoFocused}
        autoComplete={isAutoCompleted ? 'on' : 'off'}
      />
      {value && value.length > 0 && clearIcon}
    </div>
  );

  const renderSearchBar = (searchInput, searchButton) => {
    return (
      <div className={getClassNames()}>
        {/* TODO: To integrate label for accessibility purposes
          <label className="search-bar__label">Cerca su Freedome</label> 
        */}
        {searchButton}
        {searchInput}
      </div>
    );
  };

  if (asButton && variant !== SearchBarVariants.icon) {
    // TODO: Once button styles are cleaned and migrated into the new library we should think of a style for a full-width button and swap this hardcoded value
    return (
      <div className={`search-bar__toggle${width}`}>
        <button onClick={() => handleClick()}>
          {renderSearchBar(fakeSearchInput, fakeSearchButton)}
        </button>
      </div>
    );
  }

  if (asButton && variant === SearchBarVariants.icon) {
    return (
      <div onClick={() => handleClick()}>
        <BetaIcon
          name="search"
          color={isWhiteBackground ? IconColors.ghost : IconColors.regular}
          size={IconSizes[24]}
        />
      </div>
    );
  }

  return renderSearchBar(searchInput, searchButton);
}

export const SearchBarVariants = {
  primary: '--primary',
  secondary: '--secondary',
  icon: '--icon',
};

export const SearchBarWidths = {
  'contained-center': '--contained-center',
  'contained-start': '--contained-start',
  'contained-end': '--contained-end',
  full: '--full',
};

SearchBar.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf(Object.values(SearchBarVariants)),
  width: PropTypes.oneOf(Object.values(SearchBarWidths)),
  isAutoFocused: PropTypes.bool,
  isElevated: PropTypes.bool,
  isAutoCompleted: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  handleSearch: PropTypes.func,
  handleClick: PropTypes.func,
  handleOnChange: PropTypes.func,
  keyboardHandler: PropTypes.func,
  asButton: PropTypes.bool,
  isWhiteBackground: PropTypes.bool,
};

SearchBar.defaultProps = {
  className: '',
  variant: SearchBarVariants.primary,
  width: SearchBarWidths.full,
  isAutoFocused: false,
  isElevated: false,
  isAutoCompleted: false,
  placeholder: null,
  value: null,
  asButton: false,
  isWhiteBackground: false,
};
